import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState, useEffect, useCallback } from "react";
import { Package, Heart, Copy, Check, Share2 } from "lucide-react";
import { toast } from "sonner";

// Confetti component
const Confetti = () => {
  const [pieces, setPieces] = useState([]);
  
  useEffect(() => {
    const colors = ['#E07A6D', '#5A1E4F', '#F7F2EA', '#2F7D4E', '#FFD700'];
    const newPieces = [];
    
    for (let i = 0; i < 50; i++) {
      newPieces.push({
        id: i,
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 2}s`,
        duration: `${2 + Math.random() * 2}s`,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: `${8 + Math.random() * 8}px`,
        rotation: `${Math.random() * 360}deg`,
      });
    }
    setPieces(newPieces);
    
    // Clean up after animation
    const timer = setTimeout(() => setPieces([]), 4000);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <>
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="confetti-piece"
          style={{
            left: piece.left,
            animationDelay: piece.delay,
            animationDuration: piece.duration,
            backgroundColor: piece.color,
            width: piece.size,
            height: piece.size,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            transform: `rotate(${piece.rotation})`,
          }}
        />
      ))}
    </>
  );
};

// Package animation component
const PackageAnimation = () => {
  return (
    <div className="relative w-32 h-32 mx-auto">
      {/* Dotted path */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
        <path
          d="M 10 50 Q 50 20 90 50"
          fill="none"
          stroke="#E6E0D9"
          strokeWidth="2"
          strokeDasharray="4 4"
        />
      </svg>
      {/* Moving package */}
      <div className="package-moving absolute top-1/2 -translate-y-1/2">
        <Package className="h-10 w-10 text-[color:var(--pws-coral)]" />
      </div>
    </div>
  );
};

export default function ThankYou() {
  const location = useLocation();
  const { requestCode, optedIn } = location.state || {};
  const [showConfetti, setShowConfetti] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Stop confetti after initial burst
    const timer = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  const copyRequestCode = useCallback(() => {
    if (requestCode) {
      navigator.clipboard.writeText(requestCode);
      setCopied(true);
      toast.success("Code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  }, [requestCode]);

  const handleShare = useCallback(() => {
    const shareText = "Just got free underwear from Panties Without Strings—no catch, no strings. If you're in the industry, check it out.";
    const shareUrl = window.location.origin;
    
    if (navigator.share) {
      navigator.share({
        title: "Panties Without Strings",
        text: shareText,
        url: shareUrl,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Link copied to clipboard!");
    }
  }, []);

  return (
    <div className="bg-[color:var(--pws-off)] min-h-[calc(100vh-80px)] py-12 md:py-16 relative overflow-hidden">
      {/* Confetti celebration */}
      {showConfetti && <Confetti />}
      
      <div className="container-pws relative z-10">
        <div className="max-w-lg mx-auto text-center">
          {/* Package animation */}
          <PackageAnimation />

          <h1
            className="mt-6 font-['Space_Grotesk'] text-3xl sm:text-4xl font-bold text-[color:var(--pws-charcoal)]"
            data-testid="thankyou-title"
          >
            They're on their way.
          </h1>

          <p
            className="mt-3 text-lg text-[color:var(--pws-ink)]"
            data-testid="thankyou-message"
          >
            That's it. No follow-up required, no strings attached.
          </p>

          {requestCode && (
            <Card className="mt-8 border-[color:var(--pws-line)] bg-white">
              <CardContent className="p-6">
                <p className="text-sm text-[color:var(--pws-stone)] mb-3">
                  Your request code (save this to delete your info later if you want):
                </p>
                <div className="flex items-center justify-center gap-2">
                  <code
                    className="font-mono text-lg text-[color:var(--pws-charcoal)] bg-[color:var(--pws-cream)] px-4 py-2 rounded-lg"
                    data-testid="thankyou-request-code"
                  >
                    {requestCode}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={copyRequestCode}
                    className="h-10 w-10"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-[color:var(--pws-success)]" />
                    ) : (
                      <Copy className="h-4 w-4 text-[color:var(--pws-stone)]" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {optedIn && (
            <p className="mt-4 text-sm text-[color:var(--pws-stone)]" data-testid="thankyou-optin-confirm">
              We'll send occasional resources your way. Unsubscribe anytime—no hard feelings.
            </p>
          )}

          {/* Credibility Section */}
          <Card className="mt-8 border-[color:var(--pws-line)] bg-white">
            <CardContent className="p-6">
              <div className="flex items-start gap-4 text-left">
                <div className="p-2 bg-[color:var(--pws-coral)]/10 rounded-lg">
                  <Heart className="h-5 w-5 text-[color:var(--pws-coral)]" />
                </div>
                <div>
                  <p className="font-medium text-[color:var(--pws-charcoal)]">
                    Panties Without Strings
                  </p>
                  <p className="mt-1 text-sm text-[color:var(--pws-stone)]">
                    A campaign by Safe Work Coalition—worker-led, worker-focused, 
                    supporting safety, health, and rights in the industry.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Soft share ask */}
          <div className="mt-8 p-6 bg-white rounded-2xl border border-[color:var(--pws-line)]" data-testid="share-section">
            <p className="text-[color:var(--pws-ink)]">
              Know someone who could use this?
            </p>
            <p className="text-sm text-[color:var(--pws-stone)] mt-1">
              Share it with them—or don't. No pressure.
            </p>
            <Button
              variant="outline"
              onClick={handleShare}
              className="mt-4 border-[color:var(--pws-line)]"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>

          {/* Back to Home */}
          <div className="mt-8">
            <Button
              asChild
              className="btn-warm rounded-xl"
              data-testid="thankyou-home-btn"
            >
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
